import { useApi } from '@classgo/app-kit';
import { Button, Card, cx } from '@classgo/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ActAsButton } from '../../act-as';
import { staffApi } from '../../api/staff-api';
import type { ContractView, TenantView } from '../../api/types';
import { ContractForm } from '../../forms/contract-form';
import { PersonForm } from '../../forms/person-form';
import { emptySchoolForm, SchoolForm } from '../../forms/school-form';
import { useT } from '../../i18n';
import { contractFormToCreate, emptyContractForm } from '../../lib/contract-form';
import { today } from '../../lib/format';

type Progress = {
  step: 1 | 2 | 3 | 4;
  tenant: TenantView | null;
  contract: ContractView | null;
  ownerEmail: string | null;
};

/**
 * Onboarding in three steps a non-technical rep can follow: the school, its contract, its owner.
 * Each step saves as it goes, so a half-done run leaves a school that can be completed from its
 * page.
 */
export const TenantNewScreen = () => {
  const t = useT();
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<Progress>({
    step: 1,
    tenant: null,
    contract: null,
    ownerEmail: null,
  });
  const staff = staffApi(api);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['staff', 'tenants'] });

  const steps = [
    { id: 1, label: t('wizard.step.school') },
    { id: 2, label: t('wizard.step.contract') },
    { id: 3, label: t('wizard.step.owner') },
  ] as const;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <ol className="flex flex-wrap gap-2 text-sm" aria-label={t('wizard.title')}>
        {steps.map((step) => (
          <li
            key={step.id}
            aria-current={progress.step === step.id ? 'step' : undefined}
            className={cx(
              'rounded-control px-3 py-1',
              progress.step === step.id
                ? 'bg-brand-600 text-white'
                : progress.step > step.id
                  ? 'bg-success-100 text-success-700'
                  : 'bg-gray-100 text-gray-500',
            )}
          >
            {step.id}. {step.label}
          </li>
        ))}
      </ol>

      {progress.step === 1 ? (
        <Card title={t('wizard.step.school')}>
          <p className="mb-4 text-sm text-gray-500">{t('wizard.school.intro')}</p>
          <SchoolForm
            initial={emptySchoolForm()}
            mode="create"
            submitLabel={t('wizard.next')}
            onSubmit={async (values) => {
              const tenant = await staff.createTenant({
                name: values.name.trim(),
                ...(values.legalName.trim() ? { legalName: values.legalName.trim() } : {}),
                ...(values.nit.trim() ? { nit: values.nit.trim() } : {}),
                ...(values.daneCode.trim() ? { daneCode: values.daneCode.trim() } : {}),
                mainSedeName: values.mainSedeName.trim() || 'Principal',
                isDemo: values.isDemo,
              });
              await invalidate();
              setProgress({ step: 2, tenant, contract: null, ownerEmail: null });
            }}
          />
        </Card>
      ) : null}

      {progress.step === 2 && progress.tenant ? (
        <Card title={t('wizard.step.contract')}>
          <p className="mb-4 text-sm text-gray-500">
            {t('wizard.contract.intro', { tenant: progress.tenant.name })}
          </p>
          <ContractForm
            initial={emptyContractForm(today())}
            mode="create"
            submitLabel={t('wizard.next')}
            secondary={
              <Button
                variant="ghost"
                onClick={() => setProgress((current) => ({ ...current, step: 3 }))}
              >
                {t('wizard.skipContract')}
              </Button>
            }
            onSubmit={async (values) => {
              const tenant = progress.tenant;
              if (!tenant) return;
              const contract = await staff.createContract(tenant.id, contractFormToCreate(values));
              await invalidate();
              setProgress((current) => ({ ...current, step: 3, contract }));
            }}
          />
        </Card>
      ) : null}

      {progress.step === 3 && progress.tenant ? (
        <Card title={t('wizard.step.owner')}>
          <p className="mb-1 text-sm text-gray-500">
            {t('wizard.owner.intro', { tenant: progress.tenant.name })}
          </p>
          <p className="mb-4 text-sm text-gray-500">{t('owner.intro')}</p>
          <PersonForm
            kind="owner"
            submitLabel={t('wizard.next')}
            onSubmit={async (values) => {
              const tenant = progress.tenant;
              if (!tenant) return;
              await staff.createOwner(tenant.id, {
                email: values.email.trim().toLowerCase(),
                ...(values.givenName.trim() ? { givenName: values.givenName.trim() } : {}),
                ...(values.familyName.trim() ? { familyName: values.familyName.trim() } : {}),
              });
              await invalidate();
              setProgress((current) => ({ ...current, step: 4, ownerEmail: values.email }));
            }}
          />
        </Card>
      ) : null}

      {progress.step === 4 && progress.tenant ? (
        <Card>
          <h2 className="text-xl font-semibold text-gray-900">
            {t('wizard.done.title', { tenant: progress.tenant.name })}
          </h2>
          <p className="mt-2 text-sm text-gray-600">{t('wizard.done.body')}</p>
          {progress.contract ? null : (
            <p className="mt-2 rounded-control bg-warning-100 px-3 py-2 text-sm text-warning-700">
              {t('wizard.done.noContract')}
            </p>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            <Button
              onClick={() => {
                const tenant = progress.tenant;
                if (tenant) {
                  void navigate({ to: '/colegios/$tenantId', params: { tenantId: tenant.id } });
                }
              }}
            >
              {t('wizard.done.view')}
            </Button>
            <ActAsButton tenant={progress.tenant} />
          </div>
        </Card>
      ) : null}
    </div>
  );
};
